import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { SelectItem } from 'primeng/api';

import { StateService } from '@app/core/';
import { GeoLocationService, JobPositionsService, JobsService } from '@app/shared';
import { Job, JobFilter, QuickApply, SmallJob, States } from '@app/shared/shared.models';
import { Status } from './apply-now-form.models';

@Component({
  selector: 'app-shared-apply-now-form',
  templateUrl: './apply-now-form.component.html',
  styleUrls: ['./apply-now-form.component.css'],
})
export class ApplyNowFormComponent implements OnInit {
  @Input() domicileId?: number;
  @Input() jobExperience: string;
  @Input() jobId: string;
  @Input() jobTitle: string;
  @Input() recordSource?: string;
  @Input() termsConditionsStyleClass?: string;
  @Output() termsConditionsDialogClose?: EventEmitter<any> = new EventEmitter();

  applyFormPage1: FormGroup;
  careerURL: string;
  driverExperienceOptions = [
    { label: 'Less than 3 months', value: 2 },
    { label: '4-6 months', value: 3 },
    { label: 'More than 6 months', value: 3 },
  ];
  height: number;
  isLoading = false;
  jobs: Job[] = [];
  page = 1;
  selectedJobs: Job[] = [];
  showDriverLicenseExperience = false;
  smsUrl = '';
  stateOptions: SelectItem[] = [];
  status: Status;
  statusEnum = Status;
  // This output is used in the application to apply a class to keep the dialog maximized.
  readonly termsConditionsUrl = 'https://www.crengland.com/about-us/privacy-policy';
  title: string;
  violationOptions = [
    { label: '0', value: 0 },
    { label: '1', value: 1 },
    { label: '2', value: 2 },
    { label: '3', value: 3 },
    { label: '4', value: 4 },
    { label: '5', value: 5 },
    { label: '6 or more', value: 6 },
  ] as SelectItem[];
  visibleTermsAndConditions = false;
  yesNoOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false },
  ] as SelectItem[];
  width: number;

  private readonly APPLICATION_TYPE = 'P';
  private readonly careerDomain = window.__env.ngSettings.careerDomain;
  private readonly DEFAULT_RECORD_SOURCE = '7628';
  private readonly FORM_DATA_KEY = 'apply_now_form_data';
  private readonly HEIGHT_SUBSTRACTION = 250;
  private readonly MAX_JOBS = 3;
  private readonly MAX_VIOLATIONS = 4;
  private statesAbbreviations: string[] = [];
  private readonly WIDTH_SUBSTRACTION = 30;

  constructor(
    private formBuilder: FormBuilder,
    private geoLocationService: GeoLocationService,
    private jobPositionsService: JobPositionsService,
    private jobsService: JobsService,
    private stateService: StateService
  ) {}

  canSavePage1(): boolean {
    return this.applyFormPage1.valid && !this.isLoading;
  }

  gotoTop(): void {
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }

  moveToCongratulations(): void {
    // Congratulations
    this.submitFormData();
    this.status = Status.Eligible;
    this.page = 2;
    this.title = '';
    this.stateService.deleteValue(this.FORM_DATA_KEY);
  }

  ngOnInit(): void {
    this.height = window.innerHeight - this.HEIGHT_SUBSTRACTION;
    this.width = window.innerWidth - this.WIDTH_SUBSTRACTION;
    this.initForm();
    if (!this.termsConditionsStyleClass) {
      this.termsConditionsStyleClass = '';
    }
    this.status = Status.Eligible;
    this.title = this.jobTitle;
    this.jobs = [];
    this.selectedJobs = [];
    this.careerURL = `${this.careerDomain}apply?jobid=${this.jobId}`;
    if (this.domicileId && this.domicileId > 0) {
      this.careerURL += `&domicileid=${this.domicileId}`;
    }
    let sign = '?';
    if (navigator.userAgent.toLocaleLowerCase().indexOf('iphone') > -1) {
      sign = '&';
    }

    this.smsUrl = `Please call me regarding JobId: ${this.jobId}`;
    this.smsUrl = `sms:69273${sign}body=${encodeURI(this.smsUrl)}`;

    for (const state in States) {
      if (state) {
        const stateOption = { label: state, value: state };
        this.stateOptions.push(stateOption);
        this.statesAbbreviations.push(States[state]);
      }
    }
    if (!this.recordSource) {
      this.recordSource = this.DEFAULT_RECORD_SOURCE;
    }
    this.loadFormData();
    console.log(`Record Source: ${this.recordSource}`);
  }

  onHasDriverLicenseChange(event: any): void {
    this.showDriverLicenseExperience = event.value.value;
    const control = this.applyFormPage1.get('driverExperience');
    // If it has driver license
    if (this.showDriverLicenseExperience) {
      control.setValidators([Validators.required]);
    } else {
      control.setValidators([]);
    }
    control.reset();
    control.updateValueAndValidity();
  }

  validadatePage1(): void {
    if (this.has21 && this.violations < this.MAX_VIOLATIONS) {
      if (this.driverExperience >= this.mapJobExperience()) {
        // Congratulations
        this.moveToCongratulations();
      } else {
        // Not Eligle but.
        this.applyFilters();
        this.status = Status.NotEligible;
        this.title = '';

        // We will need to save the data in case the user wants to see other job.
        this.saveFormData();
      }
    } else {
      // Access Denied

      this.title = '';

      if (!this.has21) {
        this.status = Status.Denied21;
      } else {
        this.status = Status.Denied;
      }
    }
    this.gotoTop();
  }

  zipCodeSearch(): void {
    this.geoLocationService
      .zipCodeInfo(this.zipCode)
      .then((zipCodeInfo) => {
        const stateIndex = this.statesAbbreviations.indexOf(zipCodeInfo.State);
        if (stateIndex > -1) {
          const stateControl = this.applyFormPage1.get('state');
          const cityControl = this.applyFormPage1.get('city');
          stateControl.setValue(this.stateOptions[stateIndex]);
          stateControl.enable();
          cityControl.setValue(zipCodeInfo.City);
          cityControl.enable();
        }
      })
      .catch(() => {})
      .finally(() => {});
  }

  // Private getters

  private get cellPhone() {
    return this.applyFormPage1.get('cellPhone').value;
  }

  private get city() {
    return this.applyFormPage1.get('city').value;
  }

  private get email() {
    return this.applyFormPage1.get('email').value;
  }

  private get driverExperience() {
    try {
      const value = this.applyFormPage1.get('driverExperience').value.value;
      if (value) {
        return value;
      }
      return 0;
    } catch {
      return 0;
    }
  }

  private get firstName() {
    return this.applyFormPage1.get('firstName').value;
  }

  private get has21() {
    return this.applyFormPage1.get('has21').value.value;
  }

  private get hasAcceptedSMS() {
    return this.applyFormPage1.get('hasAcceptedSMS').value.value;
  }

  get hasDriverLicense(): boolean {
    return this.applyFormPage1.get('hasDriverLicense').value.value;
  }

  private get lastName() {
    return this.applyFormPage1.get('lastName').value;
  }

  private get state(): string {
    try {
      return this.applyFormPage1.get('state').value.value;
    } catch {
      return null;
    }
  }

  private get violations() {
    return this.applyFormPage1.get('violations').value.value;
  }

  private get zipCode(): string {
    return this.applyFormPage1.get('zipCode').value;
  }

  private applyFilters() {
    const filter = {
      divisions: [],
      driverTypes: [],
      experiences: [],
      hometimes: [],
      location: '',
    } as JobFilter;
    filter.experiences.push(this.driverExperience);
    if (this.zipCode) {
      filter.location = `${this.zipCode}`;
    } else {
      // We need just the state
      if (this.state) {
        filter.location = this.state;
      }
    }
    this.isLoading = true;
    this.jobsService
      .jobsFilter(filter)
      .then((apiJobs) => {
        this.jobs = apiJobs;
        if (this.jobs.length > 0) {
          this.jobs = this.jobs.splice(0, this.MAX_JOBS);
          this.jobs.forEach((c) => {
            this.selectedJobs.push(c);
          });
        }
      })
      .catch(() => {
        this.jobs = [];
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private findOptionsIndex(dropDownOption: any[], value) {
    for (let i = 0; i < dropDownOption.length; i++) {
      if (dropDownOption[i].value === value) {
        return i;
      }
    }
    return -1;
  }

  private initForm() {
    this.applyFormPage1 = this.formBuilder.group({
      cellPhone: new FormControl('', [Validators.required, Validators.pattern('[0-9]{10}')]),
      city: new FormControl({ value: '', disabled: true }),
      email: new FormControl('', [Validators.required, Validators.email]),
      driverExperience: new FormControl(''),
      firstName: new FormControl('', Validators.required),
      has21: new FormControl('', Validators.required),
      hasAcceptedSMS: new FormControl(false, Validators.required),
      hasAcceptedTerms: new FormControl(false, Validators.requiredTrue),
      hasDriverLicense: new FormControl('', Validators.required),
      lastName: new FormControl('', Validators.required),
      state: new FormControl({ value: '', disabled: true }),
      violations: new FormControl('', Validators.required),
      zipCode: new FormControl('', Validators.pattern('[0-9]{5}')),
    });
    this.applyFormPage1.reset();
    this.applyFormPage1.markAsPristine();
    this.applyFormPage1.get('has21').setValue(this.yesNoOptions[0]);
    this.applyFormPage1.get('hasDriverLicense').setValue(this.yesNoOptions[1]);
    this.applyFormPage1.get('hasAcceptedSMS').setValue(this.yesNoOptions[0]);
  }

  private loadFormData() {
    const formDataString = this.stateService.getValue(this.FORM_DATA_KEY);
    if (formDataString) {
      const formData = JSON.parse(formDataString);
      this.applyFormPage1.get('cellPhone').setValue(formData.cellPhone);
      this.applyFormPage1.get('city').setValue(formData.city);
      this.applyFormPage1.get('email').setValue(formData.email);
      const driverExperienceIndex = this.findOptionsIndex(
        this.driverExperienceOptions,
        formData.driverExperience
      );
      if (driverExperienceIndex === -1) {
        this.applyFormPage1.get('driverExperience').setValue('');
      } else {
        this.applyFormPage1
          .get('driverExperience')
          .setValue(this.driverExperienceOptions[driverExperienceIndex]);
        this.showDriverLicenseExperience = true;
      }

      this.applyFormPage1.get('firstName').setValue(formData.firstName);
      this.applyFormPage1
        .get('has21')
        .setValue(this.yesNoOptions[this.findOptionsIndex(this.yesNoOptions, formData.has21)]);
      this.applyFormPage1
        .get('hasAcceptedSMS')
        .setValue(
          this.yesNoOptions[this.findOptionsIndex(this.yesNoOptions, formData.hasAcceptedSMS)]
        );
      this.applyFormPage1.get('hasAcceptedTerms').setValue(true);
      this.applyFormPage1
        .get('hasDriverLicense')
        .setValue(
          this.yesNoOptions[this.findOptionsIndex(this.yesNoOptions, formData.hasDriverLicense)]
        );
      this.applyFormPage1.get('lastName').setValue(formData.lastName);
      const stateIndex = this.findOptionsIndex(this.stateOptions, formData.state);
      if (stateIndex === -1) {
        this.applyFormPage1.get('state').setValue('');
      } else {
        this.applyFormPage1.get('state').setValue(this.stateOptions[stateIndex]);
      }
      const violationsIndex = this.findOptionsIndex(this.violationOptions, formData.violations);
      if (violationsIndex === -1) {
        this.applyFormPage1.get('violations').setValue('');
      } else {
        this.applyFormPage1.get('violations').setValue(this.violationOptions[violationsIndex]);
      }
      this.applyFormPage1.get('zipCode').setValue(formData.zipCode);
      if (formData.zipCode && formData.zipCode !== '') {
        this.applyFormPage1.get('state').enable();
        this.applyFormPage1.get('city').enable();
      }
    }
  }

  private mapJobExperience() {
    switch (this.jobExperience) {
      case 'Need CDL':
        return 0;
      case 'Less than 3 months':
        return 2;
      case 'More than 3 months':
        return 3;
    }
  }

  private saveFormData() {
    const formData = {
      cellPhone: this.cellPhone,
      city: this.city,
      email: this.email,
      driverExperience: this.driverExperience,
      firstName: this.firstName,
      has21: this.has21,
      hasAcceptedSMS: this.hasAcceptedSMS,
      hasAcceptedTerms: true,
      hasDriverLicense: this.hasDriverLicense,
      lastName: this.lastName,
      state: this.state,
      violations: this.violations,
      zipCode: this.zipCode,
    };

    if (!this.hasDriverLicense) {
      formData.driverExperience = '';
    }

    this.stateService.setValue(this.FORM_DATA_KEY, JSON.stringify(formData));
  }

  private submitFormData(): void {
    const smallJobs: SmallJob[] = [];
    if (this.selectedJobs.length > 0) {
      this.selectedJobs.forEach((job) => {
        const newSJ = {
          jobId: job.jobId,
          domicileId: job.domicileId.toString(),
        } as SmallJob;
        smallJobs.push(newSJ);
      });
    } else {
      const newSJ = {
        jobId: this.jobId,
        domicileId: this.domicileId.toString(),
      } as SmallJob;
      smallJobs.push(newSJ);
    }

    const stateAbbr = States[this.state];

    const quickApply = {
      agreedToTerms: true,
      allowTexting: this.hasAcceptedSMS,
      applicationType: this.APPLICATION_TYPE,
      cdlExperience: String(this.driverExperience),
      cdlHolder: this.hasDriverLicense,
      cellphone: this.cellPhone,
      city: this.city,
      emailAddress: this.email,
      firstName: this.firstName,
      jobs: smallJobs,
      lastName: this.lastName,
      movingViolationCount: String(this.violations),
      over21: this.has21,
      recordSource: this.recordSource,
      state: stateAbbr,
      zipCode: String(this.zipCode),
    } as QuickApply;

    void this.jobPositionsService.quickApply(quickApply);
  }
}
